import * as React from 'react';

import type { FlashbarProps } from '@amzn/awsui-components-console';

/**
 * Notification system for displaying flash messages throughout the application.
 *
 * **TESTING GUIDANCE** (MoSCoW Priority: SHOULD):
 * This context and its hooks SHOULD be used directly in tests rather than mocked.
 * The notification system is internal application logic that benefits from integration testing.
 *
 * **Context Purpose**:
 * - Manages application-wide notification state (flashbar messages)
 * - Provides split context pattern to optimize re-renders
 * - Handles notification lifecycle (add, display, dismiss)
 * - Supports different notification types (success, error, warning, info)
 *
 * **Testing Setup**:
 * ```typescript
 * // RECOMMENDED: Use real NotificationsProvider in tests
 * import { NotificationsProvider } from '../context/NotificationsProvider';
 *
 * const renderWithNotifications = (component) => {
 *   return render(
 *     <NotificationsProvider>
 *       {component}
 *     </NotificationsProvider>
 *   );
 * };
 *
 * // Test notification interactions
 * const { getByText } = renderWithNotifications(<YourComponent />);
 * // Trigger notification
 * fireEvent.click(getByText('Show Error'));
 * // Assert notification appears
 * expect(getByText('Error occurred')).toBeInTheDocument();
 * ```
 *
 * **Why Use Directly**:
 * - Tests notification display logic and state management
 * - Verifies proper context provider setup
 * - Ensures notification dismissal works correctly
 * - Tests integration between components and notification system
 *
 * **External Dependencies to Mock**:
 * - None - this is pure React context logic
 *
 * @see {@link useNotifications} Hook for reading notifications
 * @see {@link useAddNotification} Hook for adding notifications
 */
export interface Notification {
  type: FlashbarProps.Type;
  header: string;
  message: React.ReactNode;
  deduplicationKey?: string;
  id: string;
  dismiss: () => void;
}

// We split our context into reading the notifications and adding a notification.
// This is because we don't want any component writing a notification to rerender on every notification addition,
// just the components that are reading and displaying the notifications.
export const AddNotificationsContext = React.createContext<
  | ((args: { type: FlashbarProps.Type; header: string; message: React.ReactNode; deduplicationKey?: string }) => void)
  | null
>(null);

export const NotificationsContext = React.createContext<Notification[] | null>(null);

export function useNotifications() {
  const context = React.useContext(NotificationsContext);
  if (context === null) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
}

export function useAddNotification() {
  const context = React.useContext(AddNotificationsContext);
  if (context === null) {
    throw new Error('useAddNotification must be used within a NotificationsProvider');
  }
  return context;
}
